/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Paging } from '~shared/types/paging';
import {
  ReleaseRiskSeverity,
  ReleaseRiskType,
  RiskStatus,
  RiskTransitions,
} from '~shared/types/sca';
import { UserBaseShared } from '~shared/types/users';

/** TODO refactor this to a shared type */
interface RawFacet {
  property: string;
  values: Array<{ count: number; val: string }>;
}

export type L10nMessageType = string;

export enum PackageManagers {
  Cargo = 'CARGO',
  Cocoapods = 'COCOAPODS',
  Composer = 'COMPOSER',
  Conan = 'CONAN',
  Gopkg = 'GOLANG',
  Maven = 'MAVEN',
  Npm = 'NPM',
  Nuget = 'NUGET',
  Pypi = 'PYPI',
  Rubygems = 'GEM',
  Vcpkg = 'VCPKG',
  UNKNOWN = 'UNKNOWN',
}

export enum RiskActions {
  Comment = 'COMMENT',
  Assign = 'ASSIGN',
  SetSeverity = 'SET_SEVERITY',
}

export interface Release {
  branchUuid: string; // Branch UUIDv4
  dependencyFilePaths: string[];
  dependencyRisks?: number;
  dependencyRisksKnown: boolean;
  directSummary: boolean;
  impactSeverities: ImpactSeverity[];
  key: string;
  known: boolean; // if the release version is known to some package manager
  knownPackage: boolean; // if the package is known to some package manager
  licenseExpression: string;
  newlyIntroduced: boolean;
  packageManager: string; // Package manager could be known or unknown
  packageName: string;
  packageUrl: string;
  scopeSummary: string;
  version: string;
}

export interface ImpactSeverity {
  count: number;
  val: ReleaseRiskSeverity;
}

export interface PackageManagerCount {
  packageManager: string; // Package manager could be known or unknown
  releaseCount: number;
}

export interface Dependency {
  chains?: string[][];
  direct: boolean;
  key: string;
  lockfileDependencyFilePath: string | null;
  scope: string;
  userDependencyFilePath: string | null;
}

export interface ReleaseDetailsRiskSummaryItem extends ReleaseRiskSummary, VulnerabilityBase {}

export interface ReleaseDetails extends Omit<Release, 'dependencyFilePaths'> {
  branch: BranchData;
  dependencies: Dependency[];
  issues: ReleaseDetailsRiskSummaryItem[];
}

export interface BranchData {
  /**
   * Branch string name
   */
  key: string;
  legacyProjectUuid: string;
  /**
   * Organization UUIDv4 (null for SQS)
   */
  organizationUuid: string | null;
  /**
   * Project string key
   */
  projectKey: string;
  projectName: string;
  pullRequest: boolean;
  /**
   * Branch UUIDv4
   */
  uuid: string;
}

export interface ReleaseResponse {
  branches: BranchData[];
  packageManagerCounts: PackageManagerCount[];
  page: Paging;
  releases: Release[];
}

export enum DependencyImportTypes {
  Direct = 'direct',
  Transitive = 'transitive',
}

export enum DependencyScopes {
  Development = 'development',
  Production = 'production',
}

export enum ReleaseRiskQuality {
  Security = 'SECURITY',
  Maintainability = 'MAINTAINABILITY',
}

export enum ReleaseRiskSortBy {
  Identity = 'identity',
  Severity = 'severity',
  CvssScore = 'cvssScore',
}

export enum ReleaseRiskSortOrder {
  Asc = '+',
  Desc = '-',
}

/** The basic vulnerbility information included in the list AND details */
interface VulnerabilityBase {
  cvssScore: string | null;
  cweIds: string[] | null;
  vulnerabilityId: string | null;
}

/** The smallest subset of release risk data  */
interface ReleaseRiskSummary {
  createdAt: string;
  key: string;
  manualSeverity: ReleaseRiskSeverity | null;
  originalSeverity: ReleaseRiskSeverity | null;
  quality: ReleaseRiskQuality;
  severity: ReleaseRiskSeverity;
  showIncreasedSeverityWarning: boolean;
  // SPDX license id that was prohibited (only if type=PROHIBITED_LICENSE)
  spdxLicenseId: string | null;
  type: ReleaseRiskType;
}

export interface ReleaseRiskBase extends ReleaseRiskSummary {
  actions?: RiskActions[];
  assignee: UserBaseShared | null;
  branch: BranchData;
  commentCount: number;
  release: Release;
  status: RiskStatus;
  transitions?: RiskTransitions[];
  withdrawn: boolean;
}

export interface ReleaseRisk extends ReleaseRiskBase, VulnerabilityBase {}

export interface ReleaseRiskResponse {
  branches: BranchData[];
  facets: RawFacet[];
  issuesReleases: ReleaseRisk[];
  page: Paging;
}

export interface ReleaseRiskDetails extends ReleaseRiskBase {
  vulnerability?: VulnerabilityDetails;
}

export interface VulnerabilityDetails {
  affectedPackages: VulnerabilityDetailsAffectedPackage[];
  cweIds: string[];
  description: string;
  epssPercentile: string;
  epssProbability: string;
  knownExploited: boolean;
  publishedOn: string;
  reports: VulnerabilityDetailsReport[];
  vulnerabilityId: string;
  withdrawn: boolean;
}

export interface VulnerabilityDetailsAffectedPackage {
  affectedVersions: string[];
  purl: string;
  recommendation: string;
  recommendationDetails: VulnerabilityDetailsRecommendationDetails;
  unaffectedVersions: string;
  versionOptions: VulnerabilityAlternativeVersionOption[];
}

export enum ReleaseRiskHowToFixLevel {
  COMPLETE = 'COMPLETE',
  NONE = 'NONE',
  PARTIAL = 'PARTIAL',
  UNKNOWN = 'UNKNOWN',
}

export enum ReleaseRiskHowToFixDescriptionCode {
  LATEST_COMPLETE = 'LATEST_COMPLETE',
  LATEST_PARTIAL = 'LATEST_PARTIAL',
  LATEST_PRERELEASE = 'LATEST_PRERELEASE',
  LATEST_STABLE = 'LATEST_STABLE',
  NEAREST_COMPLETE = 'NEAREST_COMPLETE',
  NEAREST_PARTIAL = 'NEAREST_PARTIAL',
  VERSION_IN_USE = 'VERSION_IN_USE',
  UNKNOWN = 'UNKNOWN',
}

export interface VulnerabilityAlternativeVersionOption {
  descriptionCode: ReleaseRiskHowToFixDescriptionCode;
  fixLevel: ReleaseRiskHowToFixLevel;
  prerelease: boolean;
  version: string;
  vulnerabilityIds: string[];
}

export interface VulnerabilityDetailsRecommendationDetails {
  falsePositiveReason: string | null;
  impactDescription: string;
  impactScore: number;
  includesDev: boolean;
  otherConditions: boolean;
  otherConditionsDescription: string | null;
  realIssue: boolean;
  specificMethodsAffected: boolean;
  specificMethodsDescription: string;
  visibility: string;
  workaroundAvailable: boolean;
  workaroundDescription: string | null;
}

export interface VulnerabilityDetailsReport {
  cvssScore: string;
  cvssSeverity: string | null;
  id: string;
  type: string;
  url: string;
  withdrawnAt: string | null;
}
export interface AnalysisResponse {
  errors: AnalysisError[];
  failedReason?: string | null;
  parsedFiles: string[];
  status: 'COMPLETED' | 'FAILED' | 'OUTDATED';
}

export interface AnalysisError {
  code: string;
  message: string;
  path?: string | null;
}

interface RiskChangelogStatusChangeRecord {
  fieldName: 'status';
  newValue: RiskStatus;
  oldValue: RiskStatus;
}

interface RiskChangelogAssigneeChangeRecord {
  fieldName: 'assigneeName';
  newValue: string;
  oldValue?: string;
}

interface RiskChangelogSeverityChangeRecord {
  fieldName: 'severity';
  newValue: ReleaseRiskSeverity;
  oldValue: ReleaseRiskSeverity;
}

interface RiskChangelogCalculatedSeverityChangeRecord {
  fieldName: 'originalSeverity';
  newValue: ReleaseRiskSeverity;
  oldValue?: ReleaseRiskSeverity;
}

export interface ReleaseRiskChangelog {
  actions: RiskChangelogAction[];
  changeData:
    | (
        | RiskChangelogStatusChangeRecord
        | RiskChangelogAssigneeChangeRecord
        | RiskChangelogSeverityChangeRecord
        | RiskChangelogCalculatedSeverityChangeRecord
      )[]
    | null;
  createdAt: string;
  htmlComment: string | null;
  key: string;
  markdownComment: string | null;
  user: UserBaseShared | null;
}

export enum RiskChangelogAction {
  EditComment = 'EDIT_COMMENT',
  DeleteComment = 'DELETE_COMMENT',
}

export interface ReleaseRiskChangelogResponse {
  changelog: ReleaseRiskChangelog[];
}

export * from '~shared/types/sca';
